import { createFileRoute, Link } from "@tanstack/react-router";
import { NonReservedNotice } from "@/components/site/NonReservedNotice";
import { RelatedServices } from "@/components/site/RelatedServices";


import {
  ArrowRight,
  Mail,
  Phone,
  MessageCircle,
  Award,
  HeartHandshake,
  Clock,
  Lock,
  Sparkles,
  Quote,
  MessageSquare,
  FileSignature,
  Handshake,
  GraduationCap,
  ShieldCheck,
  BadgeCheck,
  BookOpen,
  Scale,
  Briefcase,
  Users,
  Eye,
  UserRound,
  ChevronDown,
  Star,
} from "lucide-react";
import logoAsset from "@/assets/logo.jpg.asset.json";
import portrait1Asset from "@/assets/danny-portrait-1.jpeg.asset.json";

import mainLogo800 from "@/assets/danny-main-logo-800.webp.asset.json";
import mainLogo480 from "@/assets/danny-main-logo-480.webp.asset.json";
import mainLogo800Avif from "@/assets/danny-main-logo-800.avif.asset.json";
import mainLogo480Avif from "@/assets/danny-main-logo-480.avif.asset.json";
const logoUrl = logoAsset.url;



import { services } from "@/lib/services-data";
import { site } from "@/lib/site";

import { LeadMagnetSection } from "@/components/site/LeadMagnetSection";
import { trackSignupEvent } from "@/lib/signup-analytics";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "Legal, CCMA, Contracts & Compliance SA | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Legal advice on non-reserved matters, legal opinions, CCMA & labour support, contract drafting, POPIA & CIPC compliance, document services, ATS CV building, legal research, tutoring and corporate training across South Africa.",
      },

      {
        name: "keywords",
        content:
          "CCMA help South Africa, labour dispute assistance, unfair dismissal, disciplinary hearing representation, legal opinions, legal advice non-reserved matters, contract drafting South Africa, contract review, lease agreement template, POPIA compliance, PAIA manual, CIPC company registration, company secretarial services, B-BBEE affidavit, debt collection letter of demand, small claims court help, affidavit drafting, will drafting, ATS CV builder South Africa, CV revamp, interview coaching, legal research and writing, OSCOLA referencing, LLB tutoring, law student assignment help, corporate compliance training, immigration and Home Affairs support, consumer protection CPA, legal document services, paralegal services South Africa",
      },
      {
        property: "og:title",
        content: "Legal, CCMA, Contracts & Compliance Support in South Africa",
      },
      {
        property: "og:description",
        content:
          "Non-reserved legal advice, legal opinions, CCMA support, contracts, POPIA & CIPC compliance, document services, ATS CVs, legal research and tutoring — nationwide.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/" },
      { property: "og:image", content: "https://nkadimengdannylcs.com/__l5e/assets-v1/e095051d-6242-4d2a-a72e-391f32b57343/danny-main-logo.jpg" },
      { property: "og:image:alt", content: "Nkadimeng Danny Legal & Compliance Support logo" },
      { name: "twitter:image", content: "https://nkadimengdannylcs.com/__l5e/assets-v1/e095051d-6242-4d2a-a72e-391f32b57343/danny-main-logo.jpg" },
      { name: "twitter:title", content: "Legal, CCMA, Contracts & Compliance Support in South Africa" },
      {
        name: "twitter:description",
        content:
          "CCMA & labour, contracts, POPIA/CIPC compliance, legal opinions, document services, ATS CVs and legal research — affordable, nationwide.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://nkadimengdannylcs.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": "https://nkadimengdannylcs.com/about#founder",
              name: "Nkadimeng Danny",
              jobTitle: "Legal & Compliance Support Practitioner",
              url: "https://nkadimengdannylcs.com/about",
              alumniOf: {
                "@type": "CollegeOrUniversity",
                name: "University of South Africa (UNISA)",
              },
              hasCredential: {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "degree",
                name: "LLB",
              },
              award: [
                "Two-time published author — Record of Law (legal research and writing)",
              ],
              knowsAbout: [
                "Labour law and CCMA procedure",
                "Legal research and writing (OSCOLA)",
                "Contract drafting and review",
                "POPIA and PAIA compliance",
                "CIPC and company secretarial compliance",
              ],
            },
            {
              "@type": "LegalService",
              "@id": "https://nkadimengdannylcs.com/#legalservice",
              name: "Nkadimeng Danny Legal & Compliance Support",
              url: "https://nkadimengdannylcs.com/",
              areaServed: "ZA",
              founder: { "@id": "https://nkadimengdannylcs.com/about#founder" },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Services",
                itemListElement: services.map((s) => ({
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: s.title,
                    description: s.short,
                    url: `https://nkadimengdannylcs.com/services/${s.slug}`,
                  },
                })),
              },
            },
          ],
        }),
      },
    ],
  }),
  component: HomePage,
});


const whyUs = [
  {
    icon: Award,
    title: "Affordable services",
    body: "Transparent pricing designed to make quality legal support reachable.",
  },
  {
    icon: HeartHandshake,
    title: "Client-centred",
    body: "Real conversations, real solutions — tailored to your situation.",
  },
  {
    icon: Clock,
    title: "Responsive",
    body: "Quick replies and clear timelines so you're never left guessing.",
  },
  {
    icon: Lock,
    title: "Confidential",
    body: "Ethical, professional and POPIA-compliant handling of your matter.",
  },
];

// Client testimonials (published with written permission).
const testimonials: { quote: string; name: string; role: string; rating: number }[] = [
  {
    quote:
      "Danny helped me prepare for my CCMA conciliation from start to finish. His guidance was calm, clear and affordable — I felt heard for the first time.",
    name: "T. Mokoena",
    role: "Employee · Gauteng",
    rating: 5,
  },
  {
    quote:
      "We needed employment contracts and a POPIA policy for our small business. Turnaround was quick, pricing was transparent and the documents were spot on.",
    name: "L. van der Merwe",
    role: "SME owner · Western Cape",
    rating: 5,
  },
  {
    quote:
      "Professional, responsive and genuinely on your side. The client portal makes it easy to track everything in one place.",
    name: "S. Dlamini",
    role: "Client · KwaZulu-Natal",
    rating: 5,
  },
];

function HomePage() {
  return (
    <>
      {/* Hero — magical split-screen with animated brand logo showcase */}
      <section className="relative overflow-hidden bg-navy text-navy-foreground">
        <div className="absolute inset-0 hero-gradient" />
        {/* Massive faded logo watermark — always visible, softly floating */}
        <div
          aria-hidden
          className="absolute -right-20 top-1/2 -translate-y-1/2 hidden md:block opacity-[0.07] animate-float-slow pointer-events-none"
        >
          <picture>
            <source type="image/avif" srcSet={mainLogo800Avif.url} />
            <source type="image/webp" srcSet={mainLogo800.url} />
            <img
              src={mainLogo800.url}
              alt=""
              width={900}
              height={900}
              className="h-[38rem] w-[38rem] object-contain"
              aria-hidden
            />
          </picture>
        </div>
        {/* Mobile-only subtle watermark */}
        <div
          aria-hidden
          className="absolute inset-0 md:hidden bg-center bg-no-repeat bg-contain opacity-[0.05]"
          style={{ backgroundImage: `url(${mainLogo480.url})` }}
        />
        <div aria-hidden className="absolute inset-0 noise-overlay opacity-50" />
        {/* Gold glow accents */}
        <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-gold/20 blur-3xl animate-pulse-glow" />
        <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-3xl animate-pulse-glow" style={{ animationDelay: "2s" }} />
        {/* Drifting sparkles */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {[
            { left: "8%", delay: "0s", size: 6 },
            { left: "22%", delay: "1.4s", size: 4 },
            { left: "38%", delay: "3.1s", size: 5 },
            { left: "54%", delay: "0.7s", size: 3 },
            { left: "70%", delay: "2.3s", size: 6 },
            { left: "86%", delay: "4.0s", size: 4 },
          ].map((s, i) => (
            <span
              key={i}
              className="absolute bottom-0 rounded-full bg-gold shadow-[0_0_12px_theme(colors.amber.400)]"
              style={{
                left: s.left,
                width: s.size,
                height: s.size,
                animation: `sparkle-drift 7s ease-in ${s.delay} infinite`,
              }}
            />
          ))}
        </div>

        <div className="relative container-narrow py-20 md:py-28">
          <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] lg:items-center">
            {/* Left: copy & CTAs */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.22em] text-gold backdrop-blur">
                  <Sparkles className="h-3.5 w-3.5" />
                  Legal &amp; Compliance Support · South Africa
                </span>
                <a
                  href={`tel:${site.phoneIntl}`}
                  className="hidden md:inline-flex items-center gap-2 text-sm text-navy-foreground/80 hover:text-gold transition"
                >
                  <Phone className="h-4 w-4" /> {site.phone}
                </a>
              </div>

              <h1 className="mt-8 max-w-3xl font-display text-4xl leading-[1.05] text-navy-foreground sm:text-5xl md:text-6xl lg:text-[4.25rem]">
                Legal support that's{" "}
                <span className="gold-gradient-text">honest, accessible</span>
                <br className="hidden sm:block" />
                and built around you.
              </h1>

              <p className="mt-8 max-w-2xl text-base leading-relaxed text-navy-foreground/85 md:text-lg">
                Professional legal and compliance support across South Africa —
                labour relations, CCMA preparation, contract drafting and POPIA
                compliance — delivered with clear scope, fair pricing and no
                jargon. We provide legal information and legal advice on
                non-reserved matters, and we prepare legal opinions.
              </p>

              <div className="mt-6 max-w-2xl">
                <NonReservedNotice tone="dark" />
              </div>


              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/book"
                  className="group inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-gold-foreground shadow-gold hover:brightness-95 transition"
                >
                  Book consultation <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/auth"
                  search={{ mode: "signup" }}
                  onClick={() => trackSignupEvent("signup_click", "home_hero")}
                  className="inline-flex items-center gap-2 rounded-md bg-navy-foreground px-5 py-3 text-sm font-semibold text-navy hover:brightness-95 transition"
                >
                  <UserRound className="h-4 w-4" /> Sign up free
                </Link>
                <a
                  href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent("Hello, I'd like to enquire about your legal services.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white hover:brightness-95 transition"
                  aria-label="Chat with us on WhatsApp"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
                <a
                  href={`tel:${site.phoneIntl}`}
                  className="inline-flex items-center gap-2 rounded-md border border-navy-foreground/25 bg-navy-foreground/5 px-5 py-3 text-sm font-medium text-navy-foreground hover:bg-navy-foreground/10 transition"
                  aria-label={`Call ${site.phone}`}
                >
                  <Phone className="h-4 w-4" /> Call now
                </a>
                <a
                  href={`mailto:${site.email}?subject=${encodeURIComponent("Legal support enquiry")}`}
                  className="inline-flex items-center gap-2 rounded-md border border-navy-foreground/25 bg-navy-foreground/5 px-5 py-3 text-sm font-medium text-navy-foreground hover:bg-navy-foreground/10 transition"
                  aria-label="Email us"
                >
                  <Mail className="h-4 w-4" /> Email us
                </a>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-medium text-navy-foreground/80 hover:text-gold transition"
                >
                  Meet the Director <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {/* Trust-focused benefits */}
              <div className="mt-8 grid gap-3 sm:grid-cols-3 max-w-3xl">
                {[
                  { icon: BadgeCheck, title: "Free client portal", body: "Track matters, invoices & documents in one place." },
                  { icon: Lock, title: "POPIA-safe by default", body: "Your data stays confidential — never sold or shared." },
                  { icon: Sparkles, title: "Free CV builder + credits", body: "Sign-up bonus credits so you can try tools risk-free." },
                ].map((b) => (
                  <div
                    key={b.title}
                    className="flex items-start gap-3 rounded-lg border border-navy-foreground/15 bg-navy-foreground/5 px-4 py-3 backdrop-blur transition hover:border-gold/50 hover:bg-navy-foreground/10"
                  >
                    <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <div className="text-navy-foreground">
                      <div className="text-sm font-semibold">{b.title}</div>
                      <div className="text-xs text-navy-foreground/75 leading-snug">{b.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: animated logo showcase (magic) */}
            <div className="relative hidden lg:flex items-center justify-center">
              <div className="relative h-[28rem] w-[28rem]">
                {/* Rotating conic halo */}
                <div aria-hidden className="absolute inset-0 conic-halo animate-spin-slow" />
                {/* Counter-rotating faint halo */}
                <div aria-hidden className="absolute inset-6 conic-halo opacity-60 animate-spin-slower-reverse" />
                {/* Dashed orbit ring */}
                <div
                  aria-hidden
                  className="absolute inset-10 rounded-full border border-dashed border-gold/40 animate-spin-slower-reverse"
                />
                {/* Inner glow */}
                <div aria-hidden className="absolute inset-16 rounded-full bg-gold/15 blur-2xl animate-pulse-glow" />
                {/* The logo itself */}
                <div className="absolute inset-16 rounded-full bg-navy/70 backdrop-blur-sm border border-gold/30 shadow-lifted flex items-center justify-center animate-float-slow overflow-hidden">
                  <picture>
                    <source type="image/avif" srcSet={`${mainLogo480Avif.url} 480w, ${mainLogo800Avif.url} 800w`} sizes="320px" />
                    <source type="image/webp" srcSet={`${mainLogo480.url} 480w, ${mainLogo800.url} 800w`} sizes="320px" />
                    <img
                      src={mainLogo800.url}
                      alt="Nkadimeng Danny Legal & Compliance Support — golden ND monogram with scales of justice"
                      width={640}
                      height={640}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className="h-[86%] w-[86%] object-contain drop-shadow-[0_10px_30px_rgba(212,175,55,0.35)]"
                    />
                  </picture>
                </div>
                {/* Orbiting sparkles */}
                <div aria-hidden className="absolute inset-0 animate-spin-slow">
                  <span className="absolute left-1/2 top-0 -translate-x-1/2 h-3 w-3 rounded-full bg-gold shadow-[0_0_20px_theme(colors.amber.400)]" />
                </div>
                <div aria-hidden className="absolute inset-0 animate-spin-slower-reverse">
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-gold-soft shadow-[0_0_16px_theme(colors.amber.300)]" />
                  <span className="absolute left-2 bottom-6 h-2 w-2 rounded-full bg-gold shadow-[0_0_14px_theme(colors.amber.400)]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Lead magnet — capture emails with a free PDF */}
      <LeadMagnetSection />

      {/* Stats strip */}
      <section className="border-y border-border bg-card">
        <div className="container-narrow grid grid-cols-2 gap-y-8 gap-x-6 py-10 sm:grid-cols-4 sm:divide-x sm:divide-border">
          {[
            { k: "24h", v: "Response time" },
            { k: "9", v: "Provinces served" },
            { k: "60+", v: "SA Acts indexed" },
            { k: "100%", v: "POPIA-compliant" },
          ].map((s) => (
            <div key={s.v} className="px-2 text-center">
              <div className="font-display text-3xl leading-none text-navy md:text-4xl">
                {s.k}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* Meet Nkadimeng Danny — personal credibility */}
      <section className="container-narrow py-20">
        <div className="grid gap-12 md:grid-cols-[1fr_1.3fr] md:items-center">
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-border shadow-lifted">
              <picture>
                <source
                  type="image/avif"
                  srcSet={`${mainLogo480Avif.url} 480w, ${mainLogo800Avif.url} 800w`}
                  sizes="(min-width: 768px) 40vw, 90vw"
                />
                <source
                  type="image/webp"
                  srcSet={`${mainLogo480.url} 480w, ${mainLogo800.url} 800w`}
                  sizes="(min-width: 768px) 40vw, 90vw"
                />
                <img
                  src={mainLogo800.url}
                  alt="Nkadimeng Danny Legal & Compliance Support — golden ND monogram with scales of justice"
                  width={800}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  sizes="(min-width: 768px) 40vw, 90vw"
                  className="h-full w-full object-cover"
                />
              </picture>
            </div>
            <div className="absolute -bottom-5 -right-5 hidden h-32 w-32 overflow-hidden rounded-xl border-4 border-background shadow-lifted md:block">
              <img
                src={portrait1Asset.url}
                alt="Director portrait — Nkadimeng Danny in a three-piece suit"
                width={256}
                height={256}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div>
            <span className="gold-rule" />
            <p className="text-xs uppercase tracking-[0.25em] text-gold">
              Meet the Director
            </p>
            <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
              Nkadimeng Danny — LLB graduate, UNISA
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              You're not just hiring a brand — you're working with a person.
              I'm Danny: an LLB graduate from the University of South Africa,
              the founder of this practice, and the person who will personally
              read your matter when you reach out.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              <strong className="text-foreground">My mission</strong> is to make
              professional legal and compliance support genuinely accessible to
              ordinary South Africans — without the gatekeeping, jargon or
              cost barriers that usually stand in the way. My focus areas are
              labour relations, CCMA preparation, contract and affidavit
              drafting, POPIA compliance and filings on Court Online and
              CaseLines. Where a matter requires representation, I refer you
              to a trusted admitted attorney and stay in the loop to keep
              costs down.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                { icon: GraduationCap, label: "LLB — University of South Africa" },
                { icon: Scale, label: "Focus: labour, CCMA, contracts, POPIA" },
                { icon: Users, label: "Serves individuals, SMEs & HR teams" },
                { icon: ShieldCheck, label: "POPIA-aligned, confidential handling" },
              ].map((b) => (
                <div key={b.label} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <b.icon className="mt-0.5 h-4 w-4 text-gold" />
                  <span>{b.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground shadow-soft hover:bg-navy/90"
              >
                Read my full story <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/book"
                className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground hover:border-navy"
              >
                Book a consultation with me
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications & Achievements */}
      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="container-narrow">
          <div className="text-center">
            <span className="gold-rule mx-auto" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              Qualifications &amp; achievements
            </h2>
            <p className="mt-3 mx-auto max-w-2xl text-muted-foreground">
              A foundation built on formal study, continuous learning and
              real-world community work.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
                <GraduationCap className="h-4 w-4" /> Qualifications
              </div>
              <ul className="mt-4 space-y-3 text-sm text-foreground/85">
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span><strong>LLB</strong> — University of South Africa (UNISA)</span>
                </li>
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>Continuing legal education — labour law &amp; CCMA practice</span>
                </li>
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>Court Online &amp; CaseLines — practical filing workflows</span>
                </li>
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>POPIA compliance — privacy &amp; data-protection practice</span>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
                <Award className="h-4 w-4" /> Professional achievements
              </div>
              <ul className="mt-4 space-y-3 text-sm text-foreground/85">
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>UNISA LLB graduate</span>
                </li>
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>Founder &mdash; Nkadimeng Danny Legal &amp; Compliance Support</span>
                </li>
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>Community legal awareness talks across Limpopo &amp; Gauteng</span>
                </li>
                <li className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                  <span>Former Learner Representative Council President &mdash; drafted school constitution still in use</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About the Practice — mission, values & community commitment */}
      <section className="container-narrow py-20">
        <div className="text-center">
          <span className="gold-rule mx-auto" />
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Who we are</p>
          <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            A practice built on purpose
          </h2>
          <p className="mt-3 mx-auto max-w-3xl text-muted-foreground leading-relaxed">
            Nkadimeng Danny Legal and Compliance Support was founded with a single conviction:
            professional legal assistance should not be a privilege reserved for those who can afford
            top-tier firms. It should be available to every South African who needs it — clearly,
            affordably and with dignity.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
              <Award className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg text-navy">Our mission</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              To provide accessible, reliable and professional legal support and compliance services
              that empower individuals and businesses to navigate legal and regulatory challenges
              confidently — without the gatekeeping, jargon and cost barriers that usually stand in the way.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
              <Sparkles className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg text-navy">Our vision</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              To be a trusted legal-support partner recognised across South Africa for excellence,
              integrity and client-focused solutions — and a respected referral partner to admitted
              attorneys, mediators and compliance professionals nationwide.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg text-navy">Our promise</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Every engagement begins with a written scope and transparent quotation.
              We do not give legal advice, appear in court or hold ourselves out as attorneys —
              but where representation is required, we refer you to a trusted admitted practitioner
              and stay involved to keep costs down.
            </p>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-secondary/40 p-6 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            <div className="flex-1 space-y-4">
              <h3 className="font-display text-xl text-navy">What drives us</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This practice was born from lived experience: ten years on retail shop floors,
                five university rejection letters, and the quiet belief that justice should be
                within reach of ordinary South Africans. We serve individuals, SMEs, HR teams and
                community organisations across all nine provinces — from Limpopo to the Western Cape —
                by phone, WhatsApp, Microsoft Teams, Zoom and in person.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Beyond client work, we invest in community legal education through radio talks,
                school workshops, our NDL Justice Radio channel, and free resources like the
                NDLCS Academy and the Daily Legal Story. Good law should be readable before it is reverenced.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 md:w-[380px] shrink-0">
              {[
                { icon: ShieldCheck, label: "Integrity — we do what's right" },
                { icon: BadgeCheck, label: "Professionalism — prepared & punctual" },
                { icon: Scale, label: "Accountability — clear scope & timelines" },
                { icon: Eye, label: "Confidentiality — POPIA-aligned always" },
                { icon: HeartHandshake, label: "Client-centred — tailored solutions" },
                { icon: BookOpen, label: "Excellence — detail in every draft" },
              ].map((v) => (
                <div key={v.label} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <v.icon className="mt-0.5 h-4 w-4 text-gold shrink-0" />
                  <span>{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Case studies — anonymised capability cards */}
      <section className="container-narrow pb-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              How we help — in practice
            </h2>
            <p className="mt-3 max-w-xl text-muted-foreground">
              Anonymised examples of the typical matters we prepare. No client
              names, no confidential facts &mdash; only the kind of work we do.
            </p>
          </div>
          <Link
            to={"/case-studies" as any}
            className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-navy hover:text-gold"
          >
            View all case studies <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            {
              icon: Briefcase,
              tag: "Labour",
              title: "Disciplinary hearing preparation",
              challenge: "Employee facing a charge sheet with tight notice and no representation budget.",
              approach: "Reviewed charges under Schedule 8 LRA, drafted a defence outline, organised evidence bundle and coached on procedure.",
              outcome: "Client walked in prepared, presented a clear case and avoided dismissal — matter resolved at internal stage.",
            },
            {
              icon: Scale,
              tag: "CCMA",
              title: "CCMA conciliation & arbitration support",
              challenge: "Dismissed worker unsure how to refer a dispute or build a witness case.",
              approach: "Completed LRA 7.11 referral, prepared bundle and witness statements, briefed on conciliation-to-arbitration flow.",
              outcome: "Matter settled at conciliation with a negotiated package — no need to escalate to arbitration.",
            },
            {
              icon: ShieldCheck,
              tag: "POPIA",
              title: "POPIA compliance pack for an SME",
              challenge: "12-person business with no privacy policy, no operator agreements and a looming client audit.",
              approach: "Drafted privacy policy, consent clauses, operator agreement and a basic processing register tailored to the business.",
              outcome: "Audit passed; client retained the contract and now uses the pack as a sales differentiator.",
            },
          ].map((c) => (
            <article
              key={c.title}
              className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                  <c.icon className="h-5 w-5" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold">
                  {c.tag}
                </span>
              </div>
              <h3 className="mt-4 font-display text-lg text-navy">{c.title}</h3>
              <dl className="mt-4 space-y-3 text-sm leading-relaxed">
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Challenge</dt>
                  <dd className="mt-1 text-foreground/85">{c.challenge}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Approach</dt>
                  <dd className="mt-1 text-foreground/85">{c.approach}</dd>
                </div>
                <div className="rounded-md bg-gold/10 p-3">
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">Outcome</dt>
                  <dd className="mt-1 text-foreground">{c.outcome}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      {/* Attorney Partnership banner */}
      <section className="container-narrow py-12">
        <div className="rounded-2xl border border-gold/40 bg-card p-6 shadow-soft md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-gold">
                Attorneys &amp; admitted practitioners
              </p>
              <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
                Attorney referral &amp; partnership network
              </h2>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                Matters requiring representation are referred to admitted
                attorneys. Practitioners interested in collaboration,
                paralegal support or overflow work are invited to get in touch.
              </p>
            </div>
            <Link
              to={"/attorneys" as any}
              hash="refer"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              Refer a matter <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>


      {/* How it works */}
      <section className="container-narrow py-20">
        <div className="text-center">
          <span className="gold-rule mx-auto" />
          <h2 className="font-display text-3xl text-navy md:text-4xl">
            How it works
          </h2>
          <p className="mt-3 mx-auto max-w-xl text-muted-foreground">
            A clear, respectful process from your first message to resolution.
          </p>
        </div>
        <ol className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            {
              icon: MessageSquare,
              step: "01",
              title: "Tell us about your matter",
              body: "Reach out by phone, WhatsApp or the booking form. We'll listen and confirm scope and fees up-front.",
            },
            {
              icon: FileSignature,
              step: "02",
              title: "We prepare your case",
              body: "Documents drafted, evidence organised and a practical plan agreed — with you in the loop at every step.",
            },
            {
              icon: Handshake,
              step: "03",
              title: "Resolution & follow-through",
              body: "Assistance with preparation, negotiation, or referral to an admitted attorney where representation is required.",
            },
          ].map((s) => (
            <li
              key={s.step}
              className="relative rounded-xl border border-border bg-card p-6 shadow-soft"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="font-display text-2xl text-gold/70">{s.step}</span>
              </div>
              <h3 className="mt-4 font-display text-lg text-navy">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* Why Us — modern magic cards with gold halo */}
      <section className="relative overflow-hidden py-24">
        <div aria-hidden className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 left-0 h-96 w-96 rounded-full bg-navy/5 blur-3xl" />
        <div className="container-narrow relative">
          <div className="mx-auto max-w-2xl text-center">
            <span className="gold-rule mx-auto" />
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Why choose us</p>
            <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
              Legal support with <span className="gold-gradient-text">integrity, care & clarity</span>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Trusted by individuals, employees, employers and small businesses across South Africa.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyUs.map((item, i) => (
              <div
                key={item.title}
                className="group relative rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-lifted"
                style={{ animation: `fade-in 0.6s ease-out ${i * 0.08}s both` }}
              >
                {/* Gold halo on hover */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-gold/0 via-gold/0 to-gold/0 opacity-0 blur transition duration-500 group-hover:from-gold/30 group-hover:to-gold/0 group-hover:opacity-100"
                />
                <div className="relative">
                  <div className="relative inline-flex h-14 w-14 items-center justify-center rounded-xl bg-navy text-gold shadow-gold transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                    <span aria-hidden className="absolute inset-0 rounded-xl bg-gold/20 blur-md opacity-0 transition group-hover:opacity-100" />
                    <item.icon className="relative h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg text-navy">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                  <span aria-hidden className="mt-5 block h-px w-10 bg-gold/60 transition-all duration-300 group-hover:w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services — refined cards with shine + icon halo */}
      <section className="relative overflow-hidden bg-secondary/60 py-24">
        <div aria-hidden className="pointer-events-none absolute inset-0 noise-overlay opacity-40" />
        <div aria-hidden className="pointer-events-none absolute top-0 left-1/2 h-64 w-[42rem] -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
        <div className="container-narrow relative">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-xl">
              <span className="gold-rule" />
              <p className="text-xs uppercase tracking-[0.3em] text-gold">What we do</p>
              <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
                Services built around <span className="gold-gradient-text">real South African needs</span>
              </h2>
              <p className="mt-3 text-muted-foreground">
                Labour, CCMA, contracts, POPIA, company secretarial and more — practical, affordable support.
              </p>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-1 self-start rounded-md border border-navy/20 bg-background/60 px-4 py-2 text-sm font-medium text-navy backdrop-blur transition hover:border-gold hover:text-gold md:self-auto"
            >
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Link
                key={s.slug}
                to="/services/$slug"
                params={{ slug: s.slug }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-lifted"
                style={{ animation: `fade-in 0.6s ease-out ${i * 0.06}s both` }}
              >
                {/* Shine sweep */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-gold/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                />
                {/* Corner glow */}
                <span aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gold/0 blur-2xl transition duration-500 group-hover:bg-gold/25" />

                <div className="relative">
                  <div className="relative inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-gold/25 to-gold/5 text-navy ring-1 ring-gold/30 transition-transform duration-300 group-hover:scale-110">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-navy transition group-hover:text-gold">
                    Explore {s.title}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials — client reviews with star ratings + booking CTA */}
      <section className="relative overflow-hidden py-24">
        <div aria-hidden className="pointer-events-none absolute -top-20 left-1/4 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-navy/5 blur-3xl" />
        <div className="container-narrow relative">
          <div className="mx-auto max-w-2xl text-center">
            <span className="gold-rule mx-auto" />
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Client stories</p>
            <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
              What our clients <span className="gold-gradient-text">say about working with us</span>
            </h2>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div className="flex" aria-label="5 out of 5 average rating">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <span>5.0 average · verified clients</span>
            </div>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                className="group relative rounded-2xl border border-border bg-card p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-lifted"
                style={{ animation: `fade-in 0.6s ease-out ${i * 0.1}s both` }}
              >
                <Quote className="absolute right-5 top-5 h-8 w-8 text-gold/25" aria-hidden />
                <div className="flex" aria-label={`${t.rating} out of 5 stars`}>
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Star key={idx} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-navy to-navy/80 font-display text-sm text-gold">
                    {t.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-navy">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 relative overflow-hidden rounded-2xl border border-gold/30 bg-navy p-10 text-center text-navy-foreground shadow-lifted">
            <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gold/20 blur-3xl animate-pulse-glow" />
            <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl animate-pulse-glow" style={{ animationDelay: "1.5s" }} />
            <div className="relative">
              <Sparkles className="mx-auto h-6 w-6 text-gold" />
              <h3 className="mt-3 font-display text-2xl md:text-3xl">
                Ready to be our next <span className="gold-gradient-text">success story</span>?
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-sm text-navy-foreground/80 md:text-base">
                Book a confidential consultation — clear scope, fair pricing, no jargon.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/book"
                  className="group inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3 text-sm font-semibold text-gold-foreground shadow-gold hover:brightness-95 transition"
                >
                  Book a consultation
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </Link>
                <a
                  href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent("Hello, I'd like to book a consultation.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-[#25D366] px-6 py-3 text-sm font-semibold text-white hover:brightness-95 transition"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ — signup, privacy & payment questions */}
      <section className="container-narrow py-16" id="faq">
        <div className="text-center">
          <span className="gold-rule mx-auto" />
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Signup FAQs</p>
          <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            Common questions before you sign up
          </h2>
          <p className="mt-3 mx-auto max-w-2xl text-muted-foreground">
            Everything you need to know about accounts, your privacy and how
            payments work — with no small print.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {[
            {
              q: "Is signing up really free?",
              a: "Yes. Creating an account is 100% free — no card required. You get a client portal, a free CV builder and bonus credits to try our tools before you spend a cent.",
            },
            {
              q: "How do you protect my personal information?",
              a: "We are POPIA-aligned. Your data is encrypted in transit, stored securely, only used to serve you, and never sold or shared with third parties for marketing.",
            },
            {
              q: "What payment options do you accept?",
              a: "We accept PayFast (cards, Instant EFT, Capitec Pay, SnapScan, Zapper), manual EFT and PayPal for international clients. All invoices are issued electronically.",
            },
            {
              q: "Can I cancel or delete my account?",
              a: "Yes. You can pause a subscription or delete your account at any time from your portal, or by emailing us. We honour POPIA data-deletion requests within 30 days.",
            },
            {
              q: "Do I need a lawyer's referral to sign up?",
              a: "No. Individuals, employees, employers, HR teams and SMEs can all sign up directly. If your matter needs an admitted attorney, we refer you and stay involved to keep costs down.",
            },
            {
              q: "Will I be charged automatically?",
              a: "Never without your consent. Subscriptions renew only after you explicitly opt in, and you'll always see a clear price and scope before paying anything.",
            },
          ].map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-border bg-card p-5 shadow-soft open:shadow-lifted"
            >
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-display text-base text-navy">
                <span>{f.q}</span>
                <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-gold transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {f.a}
              </p>
            </details>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/auth"
            search={{ mode: "signup" }}
            onClick={() => trackSignupEvent("signup_click", "home_faq")}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground shadow-soft hover:bg-navy/90"
          >
            <UserRound className="h-4 w-4" /> Sign up free
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-medium text-foreground hover:border-navy"
          >
            Still have a question? Ask us
          </Link>
        </div>
      </section>

      {/* Free Resources banner */}
      <section className="container-narrow py-16">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="gold-rule" />
              <p className="text-xs uppercase tracking-[0.22em] text-gold">
                Free for everyone
              </p>
              <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
                Free resources, tools &amp; courses
              </h2>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                Take a free course, build a polished CV with our AI builder, or
                download practical legal templates — no payment required.
              </p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { to: "/education", emoji: "🎓", title: "Education Division", body: "Tutoring, writing, research and career hubs." },
              { to: "/cv-builder", emoji: "📝", title: "AI CV Builder", body: "ATS-friendly CVs in minutes." },
              { to: "/resources", emoji: "📚", title: "Resource centre", body: "Guides on labour, POPIA, contracts." },
              { to: "/legislation", emoji: "⚖️", title: "SA legislation", body: "Indexed Acts and quick references." },
            ].map((r) => (
              <Link
                key={r.to}
                to={r.to as any}
                className="group rounded-xl border border-border bg-background p-5 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-lifted"
              >
                <div className="text-2xl">{r.emoji}</div>
                <h3 className="mt-3 font-display text-base text-navy">{r.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-navy group-hover:text-gold">
                  Open <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Training banner */}
      <section className="container-narrow pb-4">
        <div className="rounded-2xl border border-gold/40 bg-navy p-6 text-navy-foreground shadow-lifted md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-gold">
                For employers &amp; HR teams
              </p>
              <h2 className="mt-2 font-display text-2xl text-navy-foreground md:text-3xl">
                Corporate training — labour law, POPIA, chairperson &amp; HR compliance
              </h2>
              <p className="mt-2 text-sm text-navy-foreground/80 md:text-base">
                On-site, virtual or hybrid workshops with working templates and
                attendance certificates for every delegate.
              </p>
            </div>
            <Link
              to={"/corporate-training" as any}
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-gold-foreground shadow-gold hover:brightness-95"
            >
              Explore programmes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Company Secretarial banner */}
      <section className="container-narrow py-8">
        <div className="rounded-2xl border border-gold/40 bg-card p-6 shadow-soft md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-gold">
                For businesses &amp; organisations
              </p>
              <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
                Company Secretarial Services
              </h2>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                CIPC filings, Beneficial Ownership, MOI drafting, statutory records
                and corporate governance — keep your company compliant.
              </p>
            </div>
            <Link
              to={"/services/company-secretarial" as any}
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              View details <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Client feedback invitation — honest placeholder, no fabricated reviews */}
      <section className="container-narrow py-16">
        <div className="rounded-2xl border border-border bg-secondary/40 p-6 md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <span className="gold-rule" />
              <p className="text-xs uppercase tracking-[0.22em] text-gold">
                Client feedback
              </p>
              <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
                Worked with us? Share your experience.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                We only display reviews with written, verified client consent.
                If we've assisted you, we'd be grateful for a short note — it
                helps other South Africans find legal support they can trust.
              </p>
            </div>
            <a
              href={`mailto:${site.email}?subject=${encodeURIComponent("Client feedback / testimonial")}&body=${encodeURIComponent("Service used:\n\nYour experience:\n\nMay we publish your first name and the service used? (yes/no):\n")}`}
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              Submit feedback <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Scope & disclaimer */}
      <section className="container-narrow pb-8">
        <div className="rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] text-gold">
            Important — scope of services
          </p>
          <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
            Legal support, not legal advice.
          </h2>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground leading-relaxed md:text-base">
            Nkadimeng Danny is an LLB graduate (UNISA) and is <strong className="text-foreground">not an admitted attorney</strong> of
            the High Court of South Africa. This practice provides paralegal,
            legal-support and compliance services. We do not give legal advice,
            do not appear in court, and do not operate a trust account. Where
            your matter requires an admitted attorney we will refer you to a
            trusted practitioner.
          </p>
          <Link
            to="/disclaimer"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-navy hover:text-gold"
          >
            Read full disclaimer &amp; POPIA notice <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow pb-24">
        <div className="overflow-hidden rounded-2xl bg-navy px-8 py-14 text-navy-foreground shadow-lifted md:px-14 md:py-16">
          <div className="grid items-center gap-8 md:grid-cols-[2fr_1fr]">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-gold">
                Get in touch
              </span>
              <h2 className="mt-3 font-display text-3xl text-navy-foreground md:text-4xl">
                Need legal assistance? Speak to us today.
              </h2>
              <p className="mt-3 text-navy-foreground/80 max-w-xl">
                Book a consultation in person, by phone, or on Microsoft Teams
                or Zoom — whichever works for you.
              </p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gold px-6 py-3 text-sm font-semibold text-gold-foreground"
              >
                Book a consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${site.phoneIntl}`}
                className="inline-flex items-center justify-center gap-2 text-sm text-navy-foreground/80 hover:text-gold"
              >
                <Phone className="h-4 w-4" />
                {site.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <RelatedServices
        exclude={["/services"]}
        limit={3}
        title="Related services & resources"
        description="Popular places to start — our full scope of work, corporate training and practical guides."
      />
    </>

  );
}
